/**
 * Question Type Selection Component for Medical On Demand
 *
 * Radio buttons on desktop web, dropdown selector on mobile/native and small screens.
 */

import { ChevronDown, AlertCircleIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { CircleIcon } from '@/components/ui/icon';
import { Radio, RadioGroup, RadioIcon, RadioIndicator, RadioLabel } from '@/components/ui/radio';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { QUESTION_TYPES } from '@/constants/medicalOnDemand';
import { useColors } from '@/hooks/useColors';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type QuestionTypeSelectProps = {
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
};

export function QuestionTypeSelect({
  control,
  errors,
  isDisabled = false,
}: QuestionTypeSelectProps) {
  const { colors } = useColors();

  const insets = useSafeAreaInsets();

  return (
    <Controller
      name="questionType"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const showError =
          !!errors.questionType?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <FormControlLabel className="mb-2">
              <FormControlLabelText
                size="sm"
                className="font-semibold leading-normal text-typography-950">
                Select your question type*
              </FormControlLabelText>
            </FormControlLabel>

            <Text className="mb-3 text-xs text-typography-500">
              Choose the option that best fits your question
            </Text>

            {/* Radio buttons - desktop only */}
            <View className="hidden md:flex">
              <RadioGroup value={value} onChange={onChange} className="flex-row flex-wrap gap-x-6">
                {QUESTION_TYPES.map((questionType) => (
                  <Radio
                    testID={`question-type-${questionType.id}`}
                    value={questionType.id}
                    key={questionType.id}
                    className="w-[calc(50%-12px)]">
                    <RadioIndicator>
                      <RadioIcon as={CircleIcon} />
                    </RadioIndicator>

                    <RadioLabel>{questionType.label}</RadioLabel>
                  </Radio>
                ))}
              </RadioGroup>
            </View>

            {/* Dropdown - mobile only */}
            <View className="md:hidden">
              <Select onValueChange={onChange} selectedValue={value}>
                <SelectTrigger
                  testID="question-type-dropdown"
                  className="flex w-full items-center justify-between gap-2 px-4 py-2">
                  <SelectInput
                    className="min-w-0 flex-1 pr-2"
                    placeholder="Select question type"
                    style={{ color: colors.typography[900] }}
                    value={QUESTION_TYPES.find((qt) => qt.id === value)?.label || ''}
                  />
                  <SelectIcon as={ChevronDown} className="shrink-0 text-typography-500" />
                </SelectTrigger>

                <SelectPortal>
                  <SelectBackdrop />
                  <SelectContent
                    className="max-h-[70vh] w-full"
                    style={{ paddingBottom: insets.bottom }}>
                    <SelectDragIndicatorWrapper>
                      <SelectDragIndicator />
                    </SelectDragIndicatorWrapper>

                    <SelectScrollView>
                      {QUESTION_TYPES.map((questionType) => (
                        <SelectItem
                          key={questionType.id}
                          label={questionType.label}
                          value={questionType.id}
                        />
                      ))}
                    </SelectScrollView>
                  </SelectContent>
                </SelectPortal>
              </Select>
            </View>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.questionType?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
